/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Every programmatic page scroll on the public site goes through here, so the
 * router's scroll manager (components/ScrollToTop.tsx), the hero buttons, the
 * header and the wizards cannot disagree about offsets, timing or smoothness.
 *
 * Three things this module knows that the call sites used to each guess:
 *
 * 1. `html { scroll-behavior: smooth }` (index.css) makes EVERY scrollTo
 *    animate, including the jump to the top of a freshly navigated page — a
 *    new page sliding up from wherever the old one was scrolled. A route
 *    change wants an instant jump; `scrollInstant` switches the smoothness
 *    off for exactly one call.
 * 2. The header hides itself on any downward scroll below `lg`
 *    (Header.tsx). A programmatic scroll towards an anchor counts as
 *    "downward", so the header slid away and the anchor landed under an
 *    empty 80 px band. `isProgrammaticScroll()` lets the header ignore the
 *    events a scroll started here produces, and the live `--header-offset`
 *    is what the anchor is placed beneath. The one state change the header
 *    cannot read from scroll events is a NEW page: it starts at its top,
 *    where the header is always in view, but with a hash the router moves
 *    straight on to the anchor and that scroll is programmatic. Whether the
 *    header ever saw the top was timing: after a mouse click the browser
 *    reported it first and the header expanded AFTER the anchor had been
 *    placed under the collapsed offset (measured 2026-10-01: the trust
 *    strip's `/hoe-wij-vergelijken#verdienmodel` landed at 52 px on desktop
 *    and 12 px on a phone, under an 80 px header); after a keyboard Enter it
 *    did not, and the new page kept the old one's collapsed header.
 *    `scrollToNewPageTop` tells the header synchronously, so every new page
 *    starts with it in view and an anchor is measured against that.
 * 3. An anchor's element often does not exist yet when the hash is read —
 *    the comparer during the providers skeleton, `#dekking` on
 *    /providers before the overview loaded, a begrip card before the
 *    list arrived. `scrollToAnchor` waits for it, for a bounded time.
 */

/** Visible gap between the header's bottom edge and a scrolled-to anchor. */
const ANCHOR_GAP_PX = 12;
/** How long a hash target is waited for before the scroll is given up. A
 *  slow connection can take this long to deliver the data that renders it;
 *  the wait is cut short the moment the visitor scrolls on their own. */
const ANCHOR_WAIT_MS = 8000;
/** Input that means "the visitor is navigating the page themselves". */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
/** A smooth scroll never takes longer than this; the flag clears afterwards. */
const PROGRAMMATIC_WINDOW_MS = 900;
/** Poll interval while waiting for an anchor. A timer, not rAF: a background
 *  or embedded tab throttles animation frames to a standstill, and the wait
 *  must still end. */
const ANCHOR_POLL_MS = 50;

let programmaticUntil = 0;

/** True while a scroll started by this module may still be producing events. */
export const isProgrammaticScroll = (): boolean => Date.now() < programmaticUntil;

const markProgrammatic = (): void => {
  programmaticUntil = Date.now() + PROGRAMMATIC_WINDOW_MS;
};

/** The live `--header-offset` (index.css), in pixels. 80 when it cannot be read. */
export const headerOffsetPx = (): number => {
  if (typeof window === 'undefined') return 80;
  const root = document.documentElement;
  const raw = getComputedStyle(root).getPropertyValue('--header-offset').trim();
  if (!raw) return 80;
  const value = parseFloat(raw);
  if (!Number.isFinite(value)) return 80;
  if (raw.endsWith('rem')) {
    const rem = parseFloat(getComputedStyle(root).fontSize) || 16;
    return value * rem;
  }
  return value;
};

/** Jump without the CSS smooth behaviour, for one call. */
export const scrollInstant = (top: number, left = 0): void => {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  const prev = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  // The two-argument form is what the oldest supported browsers and the
  // existing unit tests expect; the inline override above is what keeps it
  // instant under `html { scroll-behavior: smooth }`.
  window.scrollTo(left, top);
  root.style.scrollBehavior = prev;
};

/** Told, synchronously, that the router opened a page at its top (point 2 above). */
const newPageTopListeners = new Set<() => void>();

/**
 * Subscribe to "the router opened a new page at its top". The header listens
 * (Header.tsx): it shows itself and publishes the full `--header-offset`
 * before anything is measured against it. Returns the unsubscribe.
 */
export const onNewPageTop = (listener: () => void): (() => void) => {
  newPageTopListeners.add(listener);
  return () => {
    newPageTopListeners.delete(listener);
  };
};

/**
 * The router's jump to the top of a page it just opened (ScrollToTop.tsx):
 * instant, then every `onNewPageTop` listener, before this returns — so an
 * anchor scroll started right after it (`scrollToAnchor`) reads the header
 * offset the new page starts with, not the one the old page left behind.
 */
export const scrollToNewPageTop = (): void => {
  scrollInstant(0);
  for (const listener of newPageTopListeners) listener();
};

/**
 * Smooth scroll to an absolute document position — a jump for a visitor who
 * asked for less motion (WCAG 2.3.3). The CSS rule that honours that setting
 * (index.css) cannot: an explicit `behavior: 'smooth'` overrides
 * `scroll-behavior`, so every anchor scroll animated anyway, and a "Vergelijken"
 * tap on a phone travels a whole hero (2026-10-14).
 */
export const scrollSmooth = (top: number): void => {
  if (typeof window === 'undefined') return;
  markProgrammatic();
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    scrollInstant(Math.max(0, top));
    return;
  }
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
};

/**
 * `el.scrollIntoView(options)` for a call site that holds the element. The
 * browser places it under the header by itself: `scroll-padding-top` on the
 * root is the header's live height plus ANCHOR_GAP_PX (index.css), the same
 * spot `scrollToAnchor` computes. What the browser cannot know is that this
 * scroll is not the visitor reading — marked here, so the header does not
 * flip while it runs and slide back over the element it just brought up.
 */
export const scrollElementIntoView = (
  el: Element | null | undefined,
  options: ScrollIntoViewOptions = { block: 'start' }
): void => {
  if (!el) return;
  markProgrammatic();
  el.scrollIntoView(options);
};

/**
 * The hash of every "Vergelijken" call-to-action (getComparerHref in
 * compareAnchor.ts): where the comparison STARTS on the screen it is opened
 * on. No element carries this id — the browser never jumps to it by itself —
 * and it is resolved when the scroll runs, never at render, because the bake
 * cannot know the viewport (resolveAnchorElement below).
 */
export const COMPARER_START_ID = 'start';

/**
 * Tailwind's `sm` (40rem): below it the phone layouts — the comparer's card,
 * its two-button bar, the compact results header.
 */
const WIDE_ENOUGH_FOR_STAP_1_QUERY = '(min-width: 40rem)';

/**
 * The comparison's start: from `sm` the top of the page, where Stap 1 sits in
 * the hero (beside the copy on a desktop); below it the results. On a phone
 * Stap 1 begins under the whole hero, about 770 px down at 375 px (measured
 * 2026-10-14), so the top of the page was one more call-to-action instead of
 * the comparison — and the filter sheet in the results bar holds the same
 * three inputs. The root element stands for the top (`anchorTargetTop`).
 */
const comparerStartElement = (): HTMLElement | null => {
  if (window.matchMedia?.(WIDE_ENOUGH_FOR_STAP_1_QUERY).matches) return document.documentElement;
  return document.getElementById('compare') || document.getElementById('vergelijken');
};

/**
 * The element a hash should bring into view. The comparer exposes two ids:
 * `#compare` on the block and an invisible `#vergelijken` span at its top for
 * native anchor jumps (the root's `scroll-padding-top` already clears the
 * header, so the span sits where the block starts); both resolve to the block.
 * `#start` (COMPARER_START_ID) is the comparison's start for this screen.
 */
export const resolveAnchorElement = (id: string): HTMLElement | null => {
  if (typeof document === 'undefined' || !id) return null;
  if (id === 'vergelijken' || id === 'compare') {
    return document.getElementById('compare') || document.getElementById('vergelijken');
  }
  if (id === COMPARER_START_ID) return comparerStartElement();
  return document.getElementById(id);
};

/**
 * Where the page must scroll to so `el` sits just under the header. The root
 * element is the top of the page (the comparison's start from `sm`).
 */
export const anchorTargetTop = (el: HTMLElement, offset?: number): number => {
  if (el === document.documentElement) return 0;
  const gap = offset ?? headerOffsetPx() + ANCHOR_GAP_PX;
  return el.getBoundingClientRect().top + window.scrollY - gap;
};

export interface ScrollToAnchorOptions {
  /** Pixels from the viewport top to the element; default = header + gap. */
  offset?: number;
  /**
   * Pixels added to that offset, for something else pinned under the header —
   * the comparer's sticky filter bar, whose height a scroll to its results
   * must clear. The header part stays live: it is read when the element is
   * found, as without this option.
   */
  extraOffset?: number;
  /** Wait at most this long for the element to appear. 0 = one attempt. */
  waitMs?: number;
  /** Jump instead of animating. */
  instant?: boolean;
}

/**
 * Scroll to `#id`, waiting for the element when the page is still building
 * itself. Returns a cancel function so a navigation that supersedes this one
 * can stop the wait.
 */
export const scrollToAnchor = (id: string, options: ScrollToAnchorOptions = {}): (() => void) => {
  if (typeof window === 'undefined') return () => {};
  const { offset, extraOffset = 0, waitMs = ANCHOR_WAIT_MS, instant = false } = options;
  let cancelled = false;
  let listening = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const deadline = Date.now() + waitMs;
  const cancel = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (listening) {
      listening = false;
      for (const type of USER_SCROLL_EVENTS) window.removeEventListener(type, cancel);
    }
  };
  const attempt = () => {
    timer = null;
    if (cancelled) return;
    const el = resolveAnchorElement(id);
    if (el) {
      const top = anchorTargetTop(el, offset) - extraOffset;
      cancel();
      if (instant) scrollInstant(top);
      else scrollSmooth(top);
      return;
    }
    if (Date.now() >= deadline) {
      cancel();
      return;
    }
    if (!listening) {
      // Still waiting for the element: the first touch, wheel or key from the
      // visitor ends the wait — a page that jumps away from where someone has
      // started reading is worse than an anchor that is not honoured.
      listening = true;
      for (const type of USER_SCROLL_EVENTS) {
        window.addEventListener(type, cancel, { passive: true });
      }
    }
    timer = setTimeout(attempt, ANCHOR_POLL_MS);
  };
  attempt();
  return cancel;
};
